import { TFunction } from 'i18next';
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import {
  TranscoderAudioProfile,
  TranscoderVideoProfile,
  transcoderAudioProfileApi,
  transcoderVideoProfileApi,
  TranscoderAudioProfileResponse,
  TranscoderVideoProfileResponse,
  TypedDispatch,
} from '@vxui/common';
import { SelectBox } from '@vxui/components';
import { selectBoxStyle } from './profileForm.styles';
import { SelectFormProps } from './profileForm.types';
import {
  audioCodecsItems,
  channelItems,
  deinterlacingAlgorithmItems,
  fpsItems,
  outputFormatItems,
  resolutionItems,
  sampleRatesItems,
  videoCodecsItems,
  TSelectItem,
} from '../../../models';

const SelectMenuItem = (item: TSelectItem): ReactNode => (
  <MenuItem key={item.label} value={item.value}>
    {item.label}
  </MenuItem>
);

export const Channels = (t: TFunction): ReactNode[] => {
  return channelItems(t).map(SelectMenuItem);
};

export const Resolutions = resolutionItems.map(SelectMenuItem);

export const DeinterlacingAlgorithms = deinterlacingAlgorithmItems.map(SelectMenuItem);

export const FpsOptions = fpsItems.map(SelectMenuItem);

export const VideoCodecs = Object.values(videoCodecsItems).map(SelectMenuItem);

export const AudioCodecs = audioCodecsItems.map(SelectMenuItem);

export const SampleRates = sampleRatesItems.map(SelectMenuItem);

export const Profiles = (codec: string) => videoCodecsItems[codec]?.profiles?.map(SelectMenuItem) ?? [];

export const OutputFormats = outputFormatItems.map(SelectMenuItem);

export const InputForm = (props: TextFieldProps): ReactElement => {
  const { id = props.id ?? props.name, value = props.value ?? '', ...textFieldProps } = props;
  return <TextField id={id} {...textFieldProps} value={value} />;
};

export const SelectForm = ({
  error,
  label,
  value,
  selectItems,
  placeholder,
  onChangeHandler,
}: SelectFormProps): ReactElement => {
  return (
    <FormControl
      error={!!error && true}
      sx={theme => ({
        '.MuiFormControl-root': {
          marginTop: '0',
          marginRight: '0px !important',
          fieldset: {
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.primary[60]}`,
          },
          '.MuiSelect-select': {
            '.Mui-placeholder': {
              color: theme.palette.primary[60],
              opacity: 1,
            },
          },
        },
      })}
    >
      <Box display="flex">
        <SelectBox
          label={label}
          value={value}
          onChange={onChangeHandler}
          placeholder={placeholder}
          size="small"
          sx={(theme: Theme) => selectBoxStyle(theme.palette.primary[60])}
        >
          {selectItems}
        </SelectBox>
      </Box>
      {error && (
        <FormHelperText sx={{ marginTop: '2px' }}>
          <Typography variant="p12" component="span">
            {error}
          </Typography>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const audioProfileNameAlreadyUsed =
  (dispatch: TypedDispatch<typeof transcoderAudioProfileApi.reducer>) =>
  async (profile: Partial<TranscoderAudioProfile>): Promise<boolean> => {
    if (profile?.name) {
      const response = (await dispatch(
        transcoderAudioProfileApi.endpoints.getTranscoderAudioProfileByName.initiate(profile.name, {
          forceRefetch: true,
        }),
      )) as unknown as TranscoderAudioProfileResponse;
      return !!(response.data && response.data.id !== profile.id);
    }

    return false;
  };

export const videoProfileNameAlreadyUsed =
  (dispatch: TypedDispatch<typeof transcoderAudioProfileApi.reducer>) =>
  async (profile: Partial<TranscoderVideoProfile>): Promise<boolean> => {
    if (profile?.name) {
      const response = (await dispatch(
        transcoderVideoProfileApi.endpoints.getTranscoderVideoProfileByName.initiate(profile.name),
      )) as unknown as TranscoderVideoProfileResponse;
      return !!(response.data && response.data.id !== profile.id);
    }

    return false;
  };
